import type { FormEventHandler } from 'react';
import { memo, useCallback, useState } from 'react';

import type { KcContext } from 'keycloakify/login/KcContext';

import { Button } from '@tripleten/design-system/button';

import { PasswordInput } from 'src/shared/components/password-input/password-input.js';
import { useFieldMessage } from 'src/shared/hooks/use-field-message.js';
import { useFormValidator } from 'src/shared/hooks/use-form-validator.js';
import { usePasswordStrengthCriteria } from 'src/shared/hooks/use-password-strength-criteria.js';
import { useGetMsg } from 'src/shared/utils/kc-messages.js';

import { cnPageInternalUpdatePassword } from './page-internal-update-password.constants.js';

import './page-internal-update-password.css';

import './__form/page-internal-update-password__form.css';
import './__title/page-internal-update-password__title.css';

export interface PageInternalUpdatePasswordProps {
    kcContext: KcContext.LoginUpdatePassword;
}

export const PageInternalUpdatePassword = memo<PageInternalUpdatePasswordProps>(({ kcContext }) => {
    const [password, setPassword] = useState<string>('');

    const { msgStr, advancedMsgStr } = useGetMsg(kcContext);

    const passwordError = useFieldMessage(['password', 'password-new', 'password-confirm'], kcContext);

    const [formErrors, resetFieldError, onSubmitValidated, submitDisabled] = useFormValidator(['password-new'], {
        password: passwordError,
    });

    const onPasswordChange = useCallback<FormEventHandler<HTMLInputElement>>((e) => {
        if (!e.target) {
            return;
        }

        setPassword((e.target as HTMLInputElement).value);
    }, []);

    const passwordStrengthLevels = usePasswordStrengthCriteria(msgStr);

    return (
        <div className={cnPageInternalUpdatePassword()}>
            <h1 className={cnPageInternalUpdatePassword('title')}>{msgStr('updatePasswordTitle')}</h1>
            <form
                action={kcContext.url.loginAction}
                method="post"
                className={cnPageInternalUpdatePassword('form')}
                onSubmit={onSubmitValidated}
            >
                <PasswordInput
                    className={cnPageInternalUpdatePassword('password-new')}
                    id="password-new"
                    name="password-new"
                    errorMessage={advancedMsgStr(formErrors['password-new'] ?? '')}
                    hintText={msgStr('passwordStrengthHint')}
                    label={msgStr('password')}
                    onChange={onPasswordChange}
                    onFocus={resetFieldError}
                    password={password}
                    strengthLevels={passwordStrengthLevels}
                    value={password}
                    autoFocus={!passwordError}
                />

                <Button
                    className={cnPageInternalUpdatePassword('submit')}
                    variant="solid"
                    type="submit"
                    size="4"
                    disabled={submitDisabled}
                >
                    {msgStr('updatePasswordButton')}
                </Button>
            </form>
        </div>
    );
});
